import "./CustomerConsole.css";

import { CMSCustomer, CMSSubscription, CMSUser } from "../../types/CMSTypes";
import React, { useEffect, useMemo, useRef, useState } from "react";

import CMSCustomers from "./CMSCustomers/CMSCustomers";
import { CMSService } from "../../services/CMSService";
import CMSSubscriptions from "./CMSSubscriptions/CMSSubscriptions";
import CMSUsers from "./CMSUsers/CMSUsers";
import { selectUser } from "../../redux/reducers/userReducer";
import { useLoader } from "../../hooks/useLoader";
import { useLogoutRedirectionHome } from "../../hooks/useLogoutRedirectionHome";
import { useOnlyAdminAndCustomerUsers } from "../../hooks/useOnlyAdminAndCustomerUsers";
import { useSelector } from "react-redux";

const enum CMSPages {
  CUSTOMERS = "customers",
  USERS = "users",
  SUBSCRIPTIONS = "subscriptions",
}

const CustomerConsole = () => {
  const [activeTopic, setActiveTopic] = useState(CMSPages.CUSTOMERS);
  const [allActiveCustomer, setAllActiveCustomer] = useState<{
    customers: CMSCustomer[];
    hasMore: boolean;
  } | null>(null);
  const [allInctiveCustomer, setAllInctiveCustomer] = useState<{
    customers: CMSCustomer[];
    hasMore: boolean;
  } | null>(null);
  const [allUsersData, setAllUsersData] = useState<{
    users: CMSUser[];
    pagination: { hasMore: boolean };
  } | null>(null);
  const [allActiveSubscriptions, setAllActiveSubscriptions] = useState<{
    subscriptions: CMSSubscription[];
    pagination: { hasMore: boolean };
  } | null>(null);

  useLogoutRedirectionHome();
  useOnlyAdminAndCustomerUsers();

  const { showLoading } = useLoader();
  const user = useSelector(selectUser);

  const customersActiveCurrentPage = useRef<number>(1);
  const customersInactiveCurrentPage = useRef<number>(1);
  const usersCurrentPage = useRef<number>(1);
  const subsribersCurrentPage = useRef<number>(1);

  const isAdminUser = useMemo(
    () => user?.data?.user?.userType === "admin",
    [user?.data?.user?.userType]
  );

  useEffect(() => {
    initialCalls();
  }, [user?.data?.user?.user_id]);

  const initialCalls = () => {
    if (isAdminUser) {
      getAllActiveCustomersFromServer();
      getAllInctiveCustomersFromServer();
      getAllUsersFromServer();
      getAllSubscriptionsFromServer();
    } else {
      getCustomerByCustomerId();
      getUsersByCustomerId();
    }
  };

  const getAllActiveCustomersFromServer = async (page: number = 1) => {
    try {
      customersActiveCurrentPage.current = page;
      showLoading(true);
      const response = await CMSService.getCMSCustomers({
        status: "active",
        page,
      });
      if (page > 1) {
        setAllActiveCustomer((prev) => {
          return {
            ...response?.data,
            customers: [
              ...(prev?.customers ?? []),
              ...(response?.data?.customers ?? []),
            ],
          };
        });
      } else {
        setAllActiveCustomer(response?.data);
      }
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const getAllInctiveCustomersFromServer = async (page: number = 1) => {
    try {
      customersInactiveCurrentPage.current = page;
      showLoading(true);
      const response = await CMSService.getCMSCustomers({
        status: "inactive",
        page,
      });
      if (page > 1) {
        setAllInctiveCustomer((prev) => {
          return {
            ...response?.data,
            customers: [
              ...(prev?.customers ?? []),
              ...(response?.data?.customers ?? []),
            ],
          };
        });
      } else {
        setAllInctiveCustomer(response?.data);
      }
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const getAllUsersFromServer = async (page: number = 1) => {
    try {
      usersCurrentPage.current = page;
      showLoading(true);
      const response = await CMSService.getAllUsers({ page });
      if (page > 1) {
        setAllUsersData((prev) => {
          return {
            ...response?.data,
            users: [...(prev?.users ?? []), ...(response?.data?.users ?? [])],
          };
        });
      } else {
        setAllUsersData(response?.data);
      }
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const getAllSubscriptionsFromServer = async (page: number = 1) => {
    try {
      subsribersCurrentPage.current = page;
      showLoading(true);
      const response = await CMSService.getAllSubscriptions({ page });

      if (page > 1) {
        setAllActiveSubscriptions((prev) => {
          return {
            ...response?.data,
            subscriptions: [
              ...(prev?.subscriptions ?? []),
              ...(response?.data?.subscriptions ?? []),
            ],
          };
        });
      } else {
        setAllActiveSubscriptions(response?.data || []);
      }
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const getCustomerByCustomerId = async () => {
    try {
      showLoading(true);
      const response = await CMSService.getCustomerByCustomerId({
        customerId: user?.data?.user?.customerId || "",
      });
      setAllActiveCustomer({
        customers: [{ ...response?.data.customer }],
        hasMore: false,
      });
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const getUsersByCustomerId = async () => {
    try {
      showLoading(true);
      const response = await CMSService.getAllUsersByCustomer({
        customerId: user?.data?.user?.customerId || "",
        page: 1,
      });
      setAllUsersData(response?.data);
      showLoading(false);
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const isCustomerMenuItemActive = () => {
    return activeTopic === CMSPages.CUSTOMERS;
  };

  const isUsersMenuItemActive = () => {
    return activeTopic === CMSPages.USERS;
  };

  const renderContent = () => {
    switch (activeTopic) {
      case CMSPages.CUSTOMERS:
        return (
          <CMSCustomers
            allActiveCustomer={allActiveCustomer}
            allInctiveCustomer={allInctiveCustomer}
            getAllActiveCustomersFromServer={getAllActiveCustomersFromServer}
            getAllInctiveCustomersFromServer={getAllInctiveCustomersFromServer}
            initialCalls={initialCalls}
            isAdminUser={isAdminUser}
          />
        );
      case CMSPages.USERS:
        return (
          <CMSUsers
            allUsersData={allUsersData}
            isAdminUser={isAdminUser}
            initialCalls={initialCalls}
            getAllUsersFromServer={getAllUsersFromServer}
          />
        );
      case CMSPages.SUBSCRIPTIONS:
        return (
          <CMSSubscriptions
            allActiveSubscriptions={allActiveSubscriptions}
            getAllSubscriptionsFromServer={getAllSubscriptionsFromServer}
          />
        );
      default:
        return (
          <CMSCustomers
            allActiveCustomer={allActiveCustomer}
            allInctiveCustomer={allInctiveCustomer}
            getAllActiveCustomersFromServer={getAllActiveCustomersFromServer}
            getAllInctiveCustomersFromServer={getAllInctiveCustomersFromServer}
            initialCalls={initialCalls}
            isAdminUser={isAdminUser}
          />
        );
    }
  };

  return (
    <div className="customer-console">
      <nav className="sidebar-customer-console">
        <ul>
          <li
            style={{
              backgroundColor: isCustomerMenuItemActive()
                ? "#5C7693"
                : "transparent",
            }}
            onClick={() => setActiveTopic(CMSPages.CUSTOMERS)}
          >
            <img src="/assets/stories_side_menu_ic.svg" alt="StoriesIcon" />
            {isAdminUser ? "Customers" : "Home"}
          </li>
          <li
            style={{
              backgroundColor: isUsersMenuItemActive()
                ? "#5C7693"
                : "transparent",
            }}
            onClick={() => setActiveTopic(CMSPages.USERS)}
          >
            <img src="/assets/briefings_side_menu_ic.svg" alt="BriefingIcon" />
            Users
          </li>
          {isAdminUser && (
            <li
              style={{
                backgroundColor:
                  activeTopic === CMSPages.SUBSCRIPTIONS
                    ? "#5C7693"
                    : "transparent",
              }}
              onClick={() => setActiveTopic(CMSPages.SUBSCRIPTIONS)}
            >
              <img
                src="/assets/send_briefing_side_menu_ic.svg"
                alt="SendBriefingIcon"
              />
              Subscriptions
            </li>
          )}
        </ul>
      </nav>
      <div className="content-cms">{renderContent()}</div>
    </div>
  );
};

export default CustomerConsole;
